// src/components/auction/AuctionListoneSquadraTab.js
// Tab "Squadra" delle aste a listone: versione minimale del Market. Si sceglie
// da un listone chi mettere nella propria rosa (con il prezzo), si vede la rosa
// in composizione e si rimuovono i giocatori indicando quanti crediti si
// recuperano.
//
// "Listone consapevole": se nel tab "Lega" è stato importato l'export delle rose
// della lega, ogni riga del listone mostra da quante squadre è già posseduto il
// giocatore (→ i differenziali) e si può ordinare per "meno posseduti". Ogni
// riga mostra anche le prossime 3 partite della squadra del giocatore con la
// difficoltà.
import React, { memo, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import './AuctionListoneSquadraTab.css';
import { Search, X, Plus, Trash2, Coins, Users, Loader2, ArrowUpDown, Bandage, Pencil, Check, ChevronUp, ChevronDown } from 'lucide-react';
import TeamBadge from '../TeamBadge';
import Tooltip from '../Tooltip';
import { RoleChip, fmt } from './auctionCommon';
import { MANTRA_ROLE_THEME, MANTRA_TOKEN_ORDER } from './MantraRoleConfiguration';
import { splitRoleTokens, buildListoneEconomy } from './auctionRosterUtils';
import { useLeagueRosters } from '../../utils/leagueRostersStore';
import { buildOwnershipCountByPlayerId } from '../../utils/fantacalcioLeagueRosters';
import { buildPlayerAvailability } from '../../utils/buildPlayerAvailability';
import {
  getUpcomingFixturesWithDifficulty,
  getUpcomingCalendarDifficulty,
  getDifficultyColor,
  formatFixtureDate,
  formatDifficultyValue,
} from '../../data/serieACalendar';

const PAGE_SIZE = 60;
const FIXTURE_COUNT = 3;

const CLASSIC_ROLES = ['P', 'D', 'C', 'A'];
// Filtro ruolo: classic = P/D/C/A, mantra = i 12 token Mantra. In entrambi i
// casi multi-selezione (un giocatore passa se ha ALMENO uno dei ruoli scelti).
const MANTRA_ROLES = MANTRA_TOKEN_ORDER;

// Etichette compatte per gli "slot" per reparto della rosa
const SLOT_LABELS = { P: 'Por', D: 'Dif', C: 'Cen', A: 'Att', M: 'Mov' };
const BUCKET_LABELS = { P: 'Portieri', D: 'Difensori', C: 'Centrocampisti', A: 'Attaccanti' };

function normalizeText(value = '') {
  return String(value).toLowerCase().trim().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function getPlayerTokens(player, rolesMode) {
  if (rolesMode !== 'mantra') {
    const r = String(player?.role || 'A').trim().toUpperCase();
    return [r || 'A'];
  }
  const tokens = splitRoleTokens(player?.role);
  return tokens.length ? tokens : ['A'];
}

// Chip "gioca?" (probabili formazioni) e chip infortunio, stessa misura del
// chip possesso in lega. Il tooltip dell'infortunio dice fino a quando.
function AvailabilityChips({ availability, manualInjury }) {
  const lineup = availability?.lineup;
  const injury = availability?.injury || (manualInjury ? { reason: null, expectedReturn: null, manual: true } : null);
  const prob = Number(lineup?.prob);
  const hasProb = lineup && Number.isFinite(prob);
  const tone = hasProb ? getPlayProbabilityTone(prob) : '';
  return (
    <>
      <Tooltip
        as="span"
        className={`lst-own lst-own--sm lst-prob ${tone}`}
        title="Prossima partita"
        description={hasProb
          ? `${lineup.status === 'starter' ? 'Titolare' : 'Panchina'} nelle probabili formazioni (${prob}%)`
          : 'Probabili formazioni non disponibili per questo giocatore'}
      >
        {hasProb ? `${prob}%` : '—'}
      </Tooltip>
      {injury && (
        <Tooltip
          as="span"
          className="lst-own lst-own--sm lst-inj"
          title={injury.expectedReturn ? `Infortunato · rientro previsto: ${injury.expectedReturn}` : 'Infortunato · rientro non stimato'}
          description={injury.manual ? 'Segnalato da te come indisponibile' : injury.reason || undefined}
        >
          <Bandage size={11} />
          {injury.expectedReturn ? injury.expectedReturn : 'Inf.'}
        </Tooltip>
      )}
    </>
  );
}

function getPlayerBucket(tokens) {
  if (tokens.includes('P') && tokens.length === 1) return 'P';
  if (tokens.some((t) => ['Dd', 'Dc', 'Ds', 'B'].includes(t))) return 'D';
  if (tokens.some((t) => ['E', 'M', 'C', 'W', 'T'].includes(t))) return 'C';
  if (tokens.some((t) => ['A', 'Pc'].includes(t))) return 'A';
  return tokens.includes('P') ? 'P' : 'A';
}

// ─── Chip ruolo (classic o token mantra) ────────────────────────────────────
function RoleTokens({ tokens, rolesMode }) {
  if (rolesMode !== 'mantra') {
    return <RoleChip role={tokens[0]} />;
  }
  return (
    <span className="lst-role-tokens">
      {tokens.map((t) => {
        const theme = MANTRA_ROLE_THEME[t] || MANTRA_ROLE_THEME.A;
        return (
          <span
            key={t}
            className="lst-role-token"
            style={{ background: theme.bg, border: `1px solid ${theme.border}`, color: theme.color }}
          >
            {t}
          </span>
        );
      })}
    </span>
  );
}

// ─── Prossime N partite con difficoltà (colore = quanto è dura) ─────────────
function FixtureStrip({ team, count = FIXTURE_COUNT }) {
  const fixtures = useMemo(() => getUpcomingFixturesWithDifficulty(team, count), [team, count]);
  if (!fixtures.length) return <span className="lst-fix lst-fix--empty">—</span>;
  return (
    <span className="lst-fix">
      {fixtures.map((f) => (
        <span
          key={f.giornata}
          className="lst-fix__m"
          style={{ '--d': getDifficultyColor(f.difficulty) }}
          title={`G${f.giornata} · ${formatFixtureDate(f.date)} · ${f.home ? `${team}-${f.opponent}` : `${f.opponent}-${team}`} · difficoltà ${formatDifficultyValue(f.difficulty)}/5`}
        >
          <TeamBadge team={f.opponent} size={24} showTooltip={false} />
          <i>{f.home ? 'C' : 'T'}</i>
        </span>
      ))}
    </span>
  );
}

// ─── Riga del listone ───────────────────────────────────────────────────────
// Probabilità di giocare la prossima giornata (probabili formazioni), nello
// stesso formato di Val/MV/FV. Stesse soglie colore dei chip a destra.
function getPlayProbabilityTone(prob) {
  if (!Number.isFinite(prob)) return '';
  return prob >= 70 ? 'is-starter' : prob >= 40 ? 'is-doubt' : 'is-bench';
}

function PlayProbabilityStat({ entry }) {
  const prob = Number(entry?.prob);
  const known = entry && Number.isFinite(prob);
  return (
    <Tooltip
      as="span"
      className={`lst-row__prob ${known ? getPlayProbabilityTone(prob) : ''}`}
      title="Prossima giornata"
      description={known
        ? `${entry.status === 'starter' ? 'Titolare' : 'Panchina'} nelle probabili formazioni (${prob}%)`
        : 'Probabili formazioni non disponibili per questo giocatore'}
    >
      <i>Gioca</i>{known ? `${prob}%` : '—'}
    </Tooltip>
  );
}

const ListoneRow = memo(function ListoneRow({
  player, rolesMode, creditValue, onCreditChange, onAdd, onOpenDetail, disabled,
  ownedCount, totalTeams, isInjured, playProbability,
}) {
  const tokens = useMemo(() => getPlayerTokens(player, rolesMode), [player, rolesMode]);

  const submit = () => {
    const credits = Number(creditValue);
    if (!Number.isFinite(credits) || credits <= 0) return;
    onAdd(player, credits);
  };

  const showOwn = totalTeams > 0;
  const ownN = ownedCount || 0;

  return (
    <article className="lst-row">
      <div className="lst-row__main">
        <button
          type="button"
          className="lst-row__identity"
          onClick={() => onOpenDetail?.(player.id)}
          title="Apri scheda giocatore"
        >
          <TeamBadge team={player.team} size={44} />
          <RoleTokens tokens={tokens} rolesMode={rolesMode} />
          <span className="lst-row__nameWrap">
            <span className="lst-row__nameLine">
              <strong>{player.name}</strong>
              {isInjured && (
                <Tooltip as="span" className="lst-injury-icon" content="Infortunato">
                  <Bandage size={12} />
                </Tooltip>
              )}
            </span>
            <small>{player.team}</small>
          </span>
          {showOwn && (
            <span
              className={`lst-own lst-own--sm ${ownN === 0 ? 'is-differential' : ownN / totalTeams >= 0.6 ? 'is-common' : ''}`}
              title="Squadre della tua lega che hanno già questo giocatore in rosa"
            >
              {ownN}/{totalTeams}
            </span>
          )}
        </button>

        <FixtureStrip team={player.team} />

        <div className="lst-row__stats">
          <span><i>Val</i>{player.value != null ? fmt(player.value) : '—'}</span>
          <PlayProbabilityStat entry={playProbability} />
          <span><i>MV</i>{player.markavg != null ? fmt(player.markavg) : '—'}</span>
          <span><i>FV</i>{player.fmarkavg != null ? fmt(player.fmarkavg) : '—'}</span>
        </div>

        <div className="lst-row__add">
          <input
            type="number"
            min="1"
            step="1"
            inputMode="numeric"
            placeholder="cr"
            value={creditValue}
            onChange={(e) => onCreditChange(player.id, e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            disabled={disabled}
          />
          <button type="button" onClick={submit} disabled={disabled || !(Number(creditValue) > 0)} aria-label="Aggiungi in rosa">
            <Plus size={15} />
          </button>
        </div>
      </div>
    </article>
  );
});

// ─── Input crediti con frecce su/giù nello stile dell'app ───────────────────
// Le freccine native di input[type=number] seguono il tema del sistema
// operativo e non si possono ricolorare: le nascondiamo (CSS) e le
// sostituiamo con due bottoncini. value/onChange restano stringhe, come gli
// input che sostituisce (si può svuotare il campo mentre si scrive).
function CreditStepper({ value, onChange, min, step = 1, className = '', ...inputProps }) {
  const bump = (delta) => {
    const next = (Number(value) || 0) + delta;
    onChange(String(min !== undefined ? Math.max(min, next) : next));
  };
  return (
    <span className={`lst-stepper ${className}`.trim()}>
      <input
        {...inputProps}
        className="lst-stepper__input"
        type="number"
        inputMode="numeric"
        step={step}
        min={min}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="lst-stepper__btns">
        <button type="button" tabIndex={-1} aria-label="Aumenta" onClick={() => bump(step)}><ChevronUp size={11} /></button>
        <button type="button" tabIndex={-1} aria-label="Diminuisci" onClick={() => bump(-step)}><ChevronDown size={11} /></button>
      </span>
    </span>
  );
}

// ─── Overlay "quanti crediti recuperi" ─────────────────────────────────────
function RemoveOverlay({ purchase, onCancel, onConfirm, saving }) {
  const paid = toNum(purchase?.credits);
  // Proposta: la quotazione attuale se è nota (dalla rosa CSV caricata con
  // "Carica rose"), altrimenti quanto è stato pagato. Sempre modificabile.
  const hasCurrent = purchase?.currentValue !== null && purchase?.currentValue !== undefined && Number.isFinite(Number(purchase.currentValue));
  const current = hasCurrent ? toNum(purchase.currentValue) : null;
  const [value, setValue] = useState(String(hasCurrent ? current : paid));
  const name = purchase?.playerSnapshot?.name || purchase?.playerName || 'questo giocatore';

  return (
    <div className="overlay-backdrop" onClick={() => { if (!saving) onCancel(); }}>
      <div className="overlay-panel glass lst-remove-panel" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
        <div className="lst-remove-panel__head">
          <div className="lst-remove-panel__icon"><Trash2 size={18} /></div>
          <div>
            <h3>Vendi {name}</h3>
            <p>
              Quanti crediti recuperi rivendendolo? (pagato: {paid} cr
              {hasCurrent ? ` · quotazione attuale: ${current} cr` : ''})
            </p>
          </div>
        </div>

        <label className="lst-remove-panel__field">
          <CreditStepper min={0} value={value} onChange={setValue} autoFocus aria-label="Crediti recuperati" />
          <span>cr recuperati</span>
        </label>

        <div className="lst-remove-panel__actions">
          <button type="button" className="confirm-btn confirm-btn--ghost" onClick={onCancel} disabled={saving}>
            Annulla
          </button>
          <button
            type="button"
            className="confirm-btn confirm-btn--danger"
            onClick={() => onConfirm(Math.max(0, Number(value) || 0))}
            disabled={saving}
          >
            {saving ? 'Vendita…' : 'Vendi'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Export ────────────────────────────────────────────────────────────────
export default function AuctionListoneSquadraTab({
  auction,
  players = [],
  purchases = [],
  rolesMode = 'classic',
  rosterLimits = {},
  rosterMinLimits = {},
  myTeamName,
  playerCategoriesById = {},
  // { [playerId]: { status: 'starter'|'bench', prob } } dalle probabili formazioni.
  playProbabilityById = {},
  onPurchase,
  onRemovePurchaseListone,
  onOpenPlayerDetail,
  onAuctionPlayersCountChange,
  // (adjustment) => void: salva la correzione manuale dei crediti disponibili.
  onSetAvailableCredits,
  readOnly = false,
  saving = false,
}) {
  const [search, setSearch] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]); // vuoto = tutti
  const [selectedTeam, setSelectedTeam] = useState(''); // '' = tutte
  const [sortMode, setSortMode] = useState('value'); // value | mv | owned | calendar
  // Direzione per Val e MV (ri-clic sullo stesso pulsante la inverte).
  const [sortDir, setSortDir] = useState('desc');

  const chooseSort = useCallback((mode) => {
    setVisibleCount(PAGE_SIZE);
    if (mode === sortMode && (mode === 'value' || mode === 'mv')) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
      return;
    }
    setSortMode(mode);
    setSortDir('desc');
  }, [sortMode]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [creditInputs, setCreditInputs] = useState({});
  const [removeTarget, setRemoveTarget] = useState(null);
  // Bozza di mercato: vendite e acquisti "di prova", che aggiornano subito i
  // crediti disponibili e gli slot ma vengono salvati solo con "Conferma tutto".
  //   pendingSales: { [playerId]: prezzo di vendita }
  //   pendingBuys:  [{ player, credits }]
  const [pendingSales, setPendingSales] = useState({});
  const [pendingBuys, setPendingBuys] = useState([]);
  const [committing, setCommitting] = useState(false);
  // Solo su telefono/tablet (CSS ≤980px): un pannello alla volta, scelto dal
  // selettore in alto, e un avviso quando aggiungi un giocatore alla bozza
  // (la rosa non è visibile mentre sei nel listone).
  const [mobileView, setMobileView] = useState('list'); // list | roster
  const [addedToast, setAddedToast] = useState(null);
  const tabRef = useRef(null);
  useEffect(() => {
    if (!addedToast) return undefined;
    const t = setTimeout(() => setAddedToast(null), 3000);
    return () => clearTimeout(t);
  }, [addedToast]);
  const switchMobileView = (view) => {
    setMobileView(view);
    setAddedToast(null);
    const top = tabRef.current?.getBoundingClientRect().top;
    if (top !== undefined && top < 0) tabRef.current.scrollIntoView({ block: 'start' });
  };

  const isMantra = rolesMode === 'mantra';

  const { rosters: leagueRosters, playerLinks } = useLeagueRosters(auction?.id);

  const myPurchases = useMemo(
    () => (purchases || []).filter((p) => !p.deleted && String(p.teamName) === String(myTeamName)),
    [purchases, myTeamName]
  );

  // Nel listone a sinistra non compaiono né i giocatori in rosa né quelli già
  // nella bozza di acquisto.
  const myPlayerIds = useMemo(
    () => new Set([...myPurchases.map((p) => String(p.playerId)), ...pendingBuys.map((b) => String(b.player.id))]),
    [myPurchases, pendingBuys]
  );

  // Disponibilità per la prossima partita dei giocatori della colonna di
  // destra (rosa + acquisti di prova): probabili formazioni + infortuni, le
  // stesse fonti di Panoramica squadra.
  const availabilityPlayers = useMemo(() => [
    ...myPurchases.map((p) => ({ id: String(p.playerId), name: p.playerSnapshot?.name || p.playerName })),
    ...pendingBuys.map((b) => ({ id: String(b.player.id), name: b.player.name })),
  ], [myPurchases, pendingBuys]);
  const availabilityKey = availabilityPlayers.map((p) => p.id).sort().join(',');
  const [availabilityById, setAvailabilityById] = useState(() => new Map());
  useEffect(() => {
    let cancelled = false;
    buildPlayerAvailability(availabilityPlayers)
      .then((map) => { if (!cancelled) setAvailabilityById(map || new Map()); })
      .catch(() => {});
    return () => { cancelled = true; };
    // availabilityPlayers cambia identità a ogni render dei purchases: basta la chiave degli id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilityKey]);

  // Tiene aggiornato il conteggio giocatori sull'asta (chip di progresso in
  // AuctionCard): nel listone conta solo la propria rosa vs i limiti.
  useEffect(() => {
    if (!readOnly && typeof onAuctionPlayersCountChange === 'function') {
      onAuctionPlayersCountChange(myPurchases.length);
    }
  }, [myPurchases.length, onAuctionPlayersCountChange, readOnly]);

  const economy = useMemo(
    () => buildListoneEconomy({ auction, purchases, rosterLimits, rolesMode }),
    [auction, purchases, rosterLimits, rolesMode]
  );

  const hasDraft = pendingBuys.length > 0 || Object.keys(pendingSales).length > 0;

  // Economia "dopo le operazioni": stessa funzione, applicata agli acquisti
  // come sarebbero dopo la conferma (vendite = rimossi col prezzo indicato,
  // acquisti = nuovi acquisti). Così plus/minusvalenze, correzione manuale e
  // slot per ruolo restano coerenti con quanto verrà salvato.
  const projectedEconomy = useMemo(() => {
    if (!hasDraft) return economy;
    const projected = (purchases || []).map((p) => {
      const sale = pendingSales[String(p.playerId)];
      return sale === undefined || p.deleted ? p : { ...p, deleted: true, creditsRecovered: Number(sale) || 0 };
    });
    pendingBuys.forEach((b) => {
      projected.push({
        playerId: String(b.player.id), teamName: myTeamName, credits: Number(b.credits) || 0,
        playerSnapshot: b.player, role: b.player.role,
      });
    });
    return buildListoneEconomy({ auction, purchases: projected, rosterLimits, rolesMode });
  }, [hasDraft, economy, purchases, pendingSales, pendingBuys, myTeamName, auction, rosterLimits, rolesMode]);

  const draftSales = useMemo(
    () => myPurchases.filter((p) => pendingSales[String(p.playerId)] !== undefined),
    [myPurchases, pendingSales]
  );

  const discardDraft = () => { setPendingSales({}); setPendingBuys([]); };

  // Conferma: prima le vendite (liberano crediti), poi gli acquisti.
  const commitDraft = async () => {
    if (readOnly || committing) return;
    setCommitting(true);
    try {
      for (const p of draftSales) {
        // eslint-disable-next-line no-await-in-loop
        await onRemovePurchaseListone?.(String(p.playerId), Number(pendingSales[String(p.playerId)]) || 0);
      }
      for (const b of pendingBuys) {
        // eslint-disable-next-line no-await-in-loop
        await onPurchase?.({ player: b.player, teamName: myTeamName, credits: Number(b.credits) || 0 });
      }
      discardDraft();
    } finally {
      setCommitting(false);
    }
  };

  // Modifica a mano dei crediti disponibili: si salva la differenza rispetto
  // al valore calcolato (vedi buildListoneEconomy).
  const [editingCredits, setEditingCredits] = useState(false);
  const [creditsDraft, setCreditsDraft] = useState('');
  const saveAvailable = () => {
    const target = Number(creditsDraft);
    setEditingCredits(false);
    if (!Number.isFinite(target) || !onSetAvailableCredits) return;
    onSetAvailableCredits(Math.round(target - economy.computedAvailable));
  };

  // Possesso in lega per ogni giocatore (dall'export importato nel tab "Lega").
  const { countById: ownershipById, totalTeams: leagueTeamCount } = useMemo(() => {
    if (!leagueRosters?.teams) return { countById: new Map(), totalTeams: 0 };
    return buildOwnershipCountByPlayerId({ players, leagueTeams: leagueRosters.teams, playerLinks });
  }, [leagueRosters, players, playerLinks]);
  const hasLeague = leagueTeamCount > 0;

  // Difficoltà media del calendario per squadra (prossime N), per l'ordinamento.
  const calDiffByTeam = useMemo(() => {
    const m = new Map();
    (players || []).forEach((p) => {
      if (!m.has(p.team)) m.set(p.team, getUpcomingCalendarDifficulty(p.team, FIXTURE_COUNT) ?? 99);
    });
    return m;
  }, [players]);

  const roleFilterOptions = isMantra ? MANTRA_ROLES : CLASSIC_ROLES;

  // Squadre di Serie A presenti nel listone (per il filtro squadra).
  const teamOptions = useMemo(
    () => [...new Set((players || []).map((p) => p.team).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [players]
  );

  const toggleRole = useCallback((role) => {
    setVisibleCount(PAGE_SIZE);
    setSelectedRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  }, []);

  const available = useMemo(() => {
    const s = normalizeText(search);
    let list = (players || []).filter((p) => !myPlayerIds.has(String(p.id)));

    if (s) {
      list = list.filter((p) => normalizeText(p.name).startsWith(s) || normalizeText(p.team).startsWith(s));
    }
    if (selectedTeam) {
      list = list.filter((p) => p.team === selectedTeam);
    }
    if (selectedRoles.length) {
      list = list.filter((p) => getPlayerTokens(p, rolesMode).some((t) => selectedRoles.includes(t)));
    }

    const byValue = (a, b) => toNum(b.value) - toNum(a.value);
    if (sortMode === 'owned' && hasLeague) {
      list = [...list].sort((a, b) => {
        const oa = ownershipById.get(String(a.id)) ?? 0;
        const ob = ownershipById.get(String(b.id)) ?? 0;
        return oa - ob || byValue(a, b);
      });
    } else if (sortMode === 'calendar') {
      list = [...list].sort((a, b) => {
        const da = calDiffByTeam.get(a.team) ?? 99;
        const db = calDiffByTeam.get(b.team) ?? 99;
        return da - db || byValue(a, b);
      });
    } else if (sortMode === 'mv') {
      // Media voto: chi non ha ancora un voto (0 o assente) sta sempre in fondo.
      const mult = sortDir === 'asc' ? 1 : -1;
      list = [...list].sort((a, b) => {
        const ma = toNum(a.markavg);
        const mb = toNum(b.markavg);
        if (!ma !== !mb) return ma ? -1 : 1;
        return (ma - mb) * mult || byValue(a, b);
      });
    } else {
      list = [...list].sort((a, b) => (sortDir === 'asc' ? -byValue(a, b) : byValue(a, b)));
    }
    return list;
  }, [players, myPlayerIds, search, selectedTeam, selectedRoles, rolesMode, sortMode, sortDir, hasLeague, ownershipById, calDiffByTeam]);

  const visible = available.slice(0, visibleCount);

  const handleCreditChange = useCallback((playerId, value) => {
    setCreditInputs((prev) => ({ ...prev, [String(playerId)]: value }));
  }, []);

  // "+" dal listone: va nella bozza di acquisto (salvato solo alla conferma).
  const handleAdd = useCallback((player, credits) => {
    if (readOnly) return;
    setPendingBuys((prev) => [...prev.filter((b) => String(b.player.id) !== String(player.id)), { player, credits }]);
    setAddedToast({ name: player.name, credits });
    setCreditInputs((prev) => {
      const next = { ...prev };
      delete next[String(player.id)];
      return next;
    });
  }, [readOnly]);

  // Cestino in rosa: il prezzo scelto mette il giocatore "in vendita" nella bozza.
  const handleConfirmRemove = useCallback(async (recovered) => {
    if (!removeTarget) return;
    setPendingSales((prev) => ({ ...prev, [String(removeTarget.playerId)]: Math.max(0, Number(recovered) || 0) }));
    setRemoveTarget(null);
  }, [removeTarget]);

  // Rosa raggruppata per reparto
  // (con in coda gli acquisti in bozza, marcati come "nuovo")
  const rosterByBucket = useMemo(() => {
    const groups = { P: [], D: [], C: [], A: [] };
    const draftAsPurchases = pendingBuys.map((b) => ({
      playerId: String(b.player.id), playerName: b.player.name, playerTeam: b.player.team,
      credits: b.credits, playerSnapshot: b.player, isDraftBuy: true,
    }));
    [...myPurchases, ...draftAsPurchases].forEach((purchase) => {
      const tokens = getPlayerTokens(purchase.playerSnapshot || purchase, rolesMode);
      groups[getPlayerBucket(tokens)].push(purchase);
    });
    Object.values(groups).forEach((g) =>
      g.sort((a, b) => (Number(Boolean(a.isDraftBuy)) - Number(Boolean(b.isDraftBuy))) || toNum(b.credits) - toNum(a.credits))
    );
    return groups;
  }, [myPurchases, pendingBuys, rolesMode]);

  // Reparti ancora sotto il minimo di rosa + quanti giocatori mancano
  // (dopo le operazioni in bozza).
  const missingByRole = useMemo(() => {
    return Object.entries(projectedEconomy.byRole)
      .map(([r, slot]) => {
        const min = toNum(rosterMinLimits?.[r]);
        return { role: r, need: Math.max(0, min - slot.bought) };
      })
      .filter((x) => x.need > 0);
  }, [projectedEconomy, rosterMinLimits]);

  return (
    <div className="lst-tab" ref={tabRef} data-mobile-view={mobileView}>
      {/* ── Selettore pannello (solo telefono/tablet) ── */}
      <div className="lst-mswitch" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mobileView === 'list'}
          className={mobileView === 'list' ? 'is-active' : ''}
          onClick={() => switchMobileView('list')}
        >
          <Search size={14} /> Listone
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mobileView === 'roster'}
          className={mobileView === 'roster' ? 'is-active' : ''}
          onClick={() => switchMobileView('roster')}
        >
          <Users size={14} /> Squadra
          <b>{projectedEconomy.totalPlayers}{projectedEconomy.totalLimit ? `/${projectedEconomy.totalLimit}` : ''}</b>
          {hasDraft && <em className="lst-mswitch__badge">{draftSales.length + pendingBuys.length}</em>}
        </button>
        <span className={`lst-mswitch__credits ${projectedEconomy.available < 0 ? 'is-warn' : ''}`}>
          <Coins size={13} /> {projectedEconomy.available}
        </span>
      </div>

      {addedToast && mobileView === 'list' && (
        <div className="lst-toast" role="status">
          <span><b>{addedToast.name}</b> aggiunto a {addedToast.credits} cr · da confermare</span>
          <button type="button" onClick={() => switchMobileView('roster')}>Vedi squadra</button>
        </div>
      )}

      {/* ── Colonna sinistra: listone ── */}
      <section className="lst-panel lst-panel--list glass">
        <div className="lst-panel__head">
          <h2>Listone</h2>
          <span>
            {available.length} giocatori disponibili
            {hasLeague ? ` · possesso da ${leagueTeamCount} squadre della lega` : ''}
          </span>
        </div>

        <div className="lst-filters">
          <div className="lst-search">
            <Search size={15} />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setVisibleCount(PAGE_SIZE); }}
              placeholder="Cerca giocatore o squadra…"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} aria-label="Pulisci">
                <X size={14} />
              </button>
            )}
          </div>

          <div className="lst-filterline">
            <div className="lst-role-chips">
              <button
                type="button"
                className={`lst-role-chip ${selectedRoles.length === 0 ? 'is-active' : ''}`}
                onClick={() => { setSelectedRoles([]); setVisibleCount(PAGE_SIZE); }}
              >
                Tutti
              </button>
              {roleFilterOptions.map((role) => (
                <button
                  key={role}
                  type="button"
                  className={`lst-role-chip ${selectedRoles.includes(role) ? 'is-active' : ''}`}
                  onClick={() => toggleRole(role)}
                >
                  {role}
                </button>
              ))}
            </div>

            {teamOptions.length > 0 && (
              <select
                className="lst-team-select"
                value={selectedTeam}
                onChange={(e) => { setSelectedTeam(e.target.value); setVisibleCount(PAGE_SIZE); }}
                aria-label="Filtra per squadra"
              >
                <option value="">Tutte le squadre</option>
                {teamOptions.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            )}
          </div>

          <div className="lst-sort">
            <ArrowUpDown size={13} />
            <button
              type="button"
              className={sortMode === 'value' ? 'is-active' : ''}
              onClick={() => chooseSort('value')}
              title="Quotazione (Val): ri-clicca per invertire"
            >
              Val{sortMode === 'value' ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''}
            </button>
            <button
              type="button"
              className={sortMode === 'mv' ? 'is-active' : ''}
              onClick={() => chooseSort('mv')}
              title="Media voto (MV): ri-clicca per invertire"
            >
              MV{sortMode === 'mv' ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''}
            </button>
            {hasLeague && (
              <button type="button" className={sortMode === 'owned' ? 'is-active' : ''} onClick={() => chooseSort('owned')}>Meno posseduti</button>
            )}
            <button type="button" className={sortMode === 'calendar' ? 'is-active' : ''} onClick={() => chooseSort('calendar')}>Calendario</button>
          </div>
        </div>

        <div className="lst-list">
          {visible.map((player) => (
            <ListoneRow
              key={player.id}
              player={player}
              rolesMode={rolesMode}
              creditValue={creditInputs[String(player.id)] ?? ''}
              onCreditChange={handleCreditChange}
              onAdd={handleAdd}
              onOpenDetail={onOpenPlayerDetail}
              disabled={readOnly || saving}
              ownedCount={ownershipById.get(String(player.id))}
              totalTeams={leagueTeamCount}
              isInjured={playerCategoriesById?.[String(player.id)] === 'injury'}
              playProbability={playProbabilityById?.[String(player.id)]}
            />
          ))}
          {!visible.length && <div className="lst-empty">Nessun giocatore con questi filtri.</div>}
        </div>

        {available.length > visibleCount && (
          <button type="button" className="lst-load-more" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
            Mostra altri ({available.length - visibleCount})
          </button>
        )}
      </section>

      {/* ── Colonna destra: la mia squadra ── */}
      <aside className="lst-panel lst-panel--roster glass">
        <div className="lst-panel__head">
          <h2><Users size={16} /> {myTeamName || 'La mia squadra'}</h2>
          <span>
            {hasDraft && projectedEconomy.totalPlayers !== economy.totalPlayers
              ? <><s>{economy.totalPlayers}</s> → {projectedEconomy.totalPlayers}</>
              : economy.totalPlayers}
            {economy.totalLimit ? ` / ${economy.totalLimit}` : ''} giocatori
          </span>
        </div>

        <div className="lst-budget">
          <div className={`lst-budget__box lst-budget__box--edit ${editingCredits ? 'is-editing' : ''}`}>
            <span>Disponibili</span>
            {editingCredits ? (
              <form
                className="lst-credits-edit"
                onSubmit={(e) => { e.preventDefault(); saveAvailable(); }}
              >
                <CreditStepper
                  value={creditsDraft}
                  autoFocus
                  onChange={setCreditsDraft}
                  onKeyDown={(e) => { if (e.key === 'Escape') setEditingCredits(false); }}
                  aria-label="Crediti disponibili"
                />
                <button type="submit" aria-label="Salva"><Check size={13} /></button>
                <button type="button" onClick={() => setEditingCredits(false)} aria-label="Annulla"><X size={13} /></button>
              </form>
            ) : (
              <strong className={projectedEconomy.available < 0 ? 'is-warn' : ''}>
                <Coins size={13} />
                {hasDraft ? (
                  <span className="lst-draft-delta">
                    <s>{economy.available}</s> → {projectedEconomy.available}
                  </span>
                ) : economy.available}
                {!readOnly && onSetAvailableCredits && !hasDraft && (
                  <button
                    type="button"
                    className="lst-credits-edit__pen"
                    onClick={() => { setCreditsDraft(String(economy.available)); setEditingCredits(true); }}
                    aria-label="Modifica i crediti disponibili"
                  >
                    <Pencil size={12} />
                  </button>
                )}
              </strong>
            )}
            {economy.adjustment !== 0 && !editingCredits && (
              <em className="lst-credits-adj">
                corretti a mano ({economy.adjustment > 0 ? '+' : ''}{economy.adjustment})
                {!readOnly && onSetAvailableCredits && (
                  <button type="button" onClick={() => onSetAvailableCredits(0)}>ripristina i calcolati ({economy.computedAvailable})</button>
                )}
              </em>
            )}
          </div>
          <div className="lst-budget__box">
            <span>Spesi</span>
            <strong>{economy.spent + economy.realized}</strong>
          </div>
          {economy.realized !== 0 && (
            <div className="lst-budget__box">
              <span>Vendite</span>
              <strong className={economy.realized > 0 ? 'is-pos' : 'is-warn'}>
                {economy.realized > 0 ? '+' : ''}{economy.realized}
              </strong>
            </div>
          )}
          <div className="lst-budget__box">
            <span>Iniziali</span>
            <strong>{economy.initialCredits}</strong>
          </div>
        </div>

        {hasDraft && (
          <div className="lst-draft">
            <div className="lst-draft__head">
              <strong>Operazioni di prova</strong>
              <em>Non ancora salvate: crediti e slot qui sopra sono già quelli dopo le operazioni.</em>
            </div>
            {draftSales.length > 0 && (
              <div className="lst-draft__group">
                <span className="lst-draft__label is-sale">Vendite</span>
                {draftSales.map((p) => {
                  const id = String(p.playerId);
                  const sale = pendingSales[id];
                  const diff = Number(sale) - toNum(p.credits);
                  return (
                    <div key={id} className="lst-draft__row">
                      <span className="lst-draft__name">{p.playerSnapshot?.name || p.playerName}</span>
                      <span className="lst-draft__meta">pagato {toNum(p.credits)}</span>
                      <CreditStepper
                        min={0} value={sale}
                        onChange={(v) => setPendingSales((prev) => ({ ...prev, [id]: v }))}
                        aria-label="Prezzo di vendita"
                      />
                      <span className={`lst-draft__diff ${diff > 0 ? 'is-pos' : diff < 0 ? 'is-neg' : ''}`}>{diff > 0 ? '+' : ''}{Number.isFinite(diff) ? diff : 0}</span>
                      <button type="button" onClick={() => setPendingSales((prev) => { const n = { ...prev }; delete n[id]; return n; })} aria-label="Annulla vendita"><X size={13} /></button>
                    </div>
                  );
                })}
              </div>
            )}
            {pendingBuys.length > 0 && (
              <div className="lst-draft__group">
                <span className="lst-draft__label is-buy">Acquisti</span>
                {pendingBuys.map((b) => {
                  const id = String(b.player.id);
                  return (
                    <div key={id} className="lst-draft__row">
                      <span className="lst-draft__name">{b.player.name}</span>
                      <span className="lst-draft__meta">{b.player.team}</span>
                      <CreditStepper
                        min={1} value={b.credits}
                        onChange={(v) => setPendingBuys((prev) => prev.map((x) => (String(x.player.id) === id ? { ...x, credits: v } : x)))}
                        aria-label="Prezzo di acquisto"
                      />
                      <span className="lst-draft__diff">cr</span>
                      <button type="button" onClick={() => setPendingBuys((prev) => prev.filter((x) => String(x.player.id) !== id))} aria-label="Annulla acquisto"><X size={13} /></button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="lst-draft__total">
              Incassi <b>{draftSales.reduce((s, p) => s + (Number(pendingSales[String(p.playerId)]) || 0), 0)}</b> · spendi{' '}
              <b>{pendingBuys.reduce((s, b) => s + (Number(b.credits) || 0), 0)}</b> · disponibili dopo{' '}
              <b className={projectedEconomy.available < 0 ? 'is-neg' : ''}>{projectedEconomy.available}</b>
            </div>
            {projectedEconomy.available < 0 && (
              <div className="lst-draft__warn">Con queste operazioni andresti sotto zero di {-projectedEconomy.available} crediti.</div>
            )}
            <div className="lst-draft__actions">
              <button type="button" className="lst-draft__btn" onClick={discardDraft} disabled={committing}>Annulla tutto</button>
              <button
                type="button"
                className="lst-draft__btn lst-draft__btn--primary"
                onClick={commitDraft}
                disabled={committing || readOnly || pendingBuys.some((b) => !(Number(b.credits) > 0))}
              >
                {committing ? <Loader2 size={14} className="spin" /> : <Check size={14} />}
                {committing ? 'Salvo…' : `Conferma ${draftSales.length ? `${draftSales.length} vendit${draftSales.length === 1 ? 'a' : 'e'}` : ''}${draftSales.length && pendingBuys.length ? ' e ' : ''}${pendingBuys.length ? `${pendingBuys.length} acquist${pendingBuys.length === 1 ? 'o' : 'i'}` : ''}`}
              </button>
            </div>
          </div>
        )}

        <div className="lst-slots">
          {Object.entries(projectedEconomy.byRole).map(([r, slot]) => {
            const over = slot.limit > 0 && slot.bought > slot.limit;
            const under = toNum(rosterMinLimits?.[r]) > slot.bought;
            return (
              <span key={r} className={`lst-slot ${over ? 'is-over' : ''} ${under ? 'is-under' : ''}`}>
                {SLOT_LABELS[r] || r}
                <strong>{slot.bought}{slot.limit > 0 ? `/${slot.limit}` : ''}</strong>
              </span>
            );
          })}
        </div>

        {missingByRole.length > 0 && (
          <div className="lst-missing">
            Ti mancano{' '}
            {missingByRole.map((m, i) => (
              <span key={m.role}>
                {i > 0 ? ', ' : ''}<strong>{m.need}</strong> {SLOT_LABELS[m.role] || m.role}
              </span>
            ))}
            {' '}· budget <strong>{projectedEconomy.available}</strong>
          </div>
        )}

        <div className="lst-roster">
          {['P', 'D', 'C', 'A'].map((bucket) => {
            const group = rosterByBucket[bucket];
            if (!group.length) return null;
            return (
              <div key={bucket} className="lst-roster__group">
                <div className="lst-roster__groupHead">{BUCKET_LABELS[bucket]} · {group.length}</div>
                {group.map((purchase) => {
                  const snap = purchase.playerSnapshot || {};
                  const tokens = getPlayerTokens(snap.role ? snap : purchase, rolesMode);
                  const own = hasLeague ? (ownershipById.get(String(purchase.playerId)) ?? 0) : null;
                  const id = String(purchase.playerId);
                  const selling = pendingSales[id] !== undefined;
                  const buying = Boolean(purchase.isDraftBuy);
                  return (
                    <div key={id} className={`lst-roster__row ${selling ? 'is-selling' : ''} ${buying ? 'is-buying' : ''}`}>
                      <TeamBadge team={snap.team || purchase.playerTeam} size={28} />
                      <RoleTokens tokens={tokens} rolesMode={rolesMode} />
                      <span className="lst-roster__nameWrap">
                        <span className="lst-roster__name">{snap.name || purchase.playerName}</span>
                        {buying && <span className="lst-draft-tag is-buy">nuovo</span>}
                        {selling && <span className="lst-draft-tag is-sale">in vendita a {Number(pendingSales[id]) || 0}</span>}
                      </span>
                      <AvailabilityChips
                        availability={availabilityById.get(id)}
                        manualInjury={playerCategoriesById?.[id] === 'injury'}
                      />
                      {own != null && !selling && !buying && (
                        <span className={`lst-own lst-own--sm ${own === 0 ? 'is-differential' : ''}`} title="Squadre della lega che lo hanno">
                          {own}/{leagueTeamCount}
                        </span>
                      )}
                      <span className="lst-roster__credits">{toNum(purchase.credits)} cr</span>
                      {!readOnly && (selling || buying ? (
                        <button
                          type="button"
                          className="lst-roster__remove"
                          onClick={() => (buying
                            ? setPendingBuys((prev) => prev.filter((x) => String(x.player.id) !== id))
                            : setPendingSales((prev) => { const n = { ...prev }; delete n[id]; return n; }))}
                          aria-label={buying ? 'Annulla acquisto' : 'Annulla vendita'}
                        >
                          <X size={14} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="lst-roster__remove"
                          onClick={() => setRemoveTarget(purchase)}
                          aria-label="Vendi"
                        >
                          <Trash2 size={14} />
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {!myPurchases.length && !pendingBuys.length && (
            <div className="lst-roster__empty">
              {saving ? <Loader2 size={20} className="spin" /> : <Users size={22} />}
              <span>Aggiungi giocatori dal listone a sinistra.</span>
            </div>
          )}
        </div>
      </aside>

      {removeTarget && (
        <RemoveOverlay
          purchase={removeTarget}
          onCancel={() => setRemoveTarget(null)}
          onConfirm={handleConfirmRemove}
          saving={saving}
        />
      )}
    </div>
  );
}
